import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  List,
  Sparkles,
  Sun,
  Moon,
  ArrowRight,
  Calendar,
  Eye,
  ArrowLeft
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import LoginPromptModal from '../components/LoginPromptModal';
import AdBanner from '../components/AdBanner';
import SeoHead from '../components/SeoHead';
import '../styles/chapter-viewer.css';

const ArticleReader = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Core article & feed state
  const [article, setArticle] = useState(null);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Reading UX & Theme state (Default 14px font, night theme)
  const [theme, setTheme] = useState('night'); // 'night', 'sepia', 'light', 'dark'
  const [fontSize, setFontSize] = useState(14);
  const [tocOpen, setTocOpen] = useState(false);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [mobileAiOpen, setMobileAiOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // AI & Interactive state
  const [selectedText, setSelectedText] = useState('');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatMessage, setChatMessage] = useState('');

  const articleRef = useRef(null);
  const rightSidebarRef = useRef(null);
  const [sidebarHeight, setSidebarHeight] = useState(null);

  const closeMobileDrawers = () => {
    setMobileTocOpen(false);
    setMobileAiOpen(false);
  };

  // Sync article card height with right sidebar height if side pane is active
  useEffect(() => {
    const updateHeight = () => {
      if (rightSidebarRef.current && window.innerWidth > 768) {
        const h = rightSidebarRef.current.offsetHeight;
        if (h && h > 200) {
          setSidebarHeight(h);
        }
      } else {
        setSidebarHeight(null);
      }
    };

    updateHeight();
    const timer1 = setTimeout(updateHeight, 200);
    const timer2 = setTimeout(updateHeight, 600);
    window.addEventListener('resize', updateHeight);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      window.removeEventListener('resize', updateHeight);
    };
  }, [article, loading, isAuthenticated]);


  // Fetch article data & feed list
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch article by slug
      const data = await apiService.getArticleBySlug(slug);
      setArticle(data);

      // Save to last read session in localStorage
      if (data) {
        try {
          const lastReadData = {
            articleId: data.id,
            title: data.title,
            author: data.authorName,
            slug: data.slug,
            timestamp: new Date().toISOString()
          };
          localStorage.setItem('last_read_article', JSON.stringify(lastReadData));
        } catch (e) {
          console.error('Error saving last read article:', e);
        }
      }

      // Fetch feed for article stepper & drawer
      try {
        const allArticles = await apiService.getArticleFeed(null, 1, 50);
        setFeed(allArticles || []);
      } catch (feedErr) {
        console.error('Error fetching article feed list:', feedErr);
      }

    } catch (err) {
      console.error('Error loading article reader data:', err);
      setError(err.message || 'Article not found or failed to load.');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadData();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [loadData]);

  // Track Reading Progress on Scroll
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight > 0) {
        const progress = Math.min(100, Math.max(0, (window.scrollY / totalHeight) * 100));
        setScrollProgress(progress);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Text selection handler
  const handleTextSelection = () => {
    const selection = window.getSelection();
    const text = selection.toString().trim();
    if (text.length > 5) {
      setSelectedText(text);
      if (!isAuthenticated()) {
        setShowLoginPrompt(true);
      }
    }
  };

  // Run AI action
  const handleAiAction = async (actionType) => {
    if (!isAuthenticated()) {
      setShowLoginPrompt(true);
      return;
    }
    if (!selectedText && typeof actionType === 'string' && !['translate', 'commentary', 'surgical'].includes(actionType)) {
      // User typed a custom question
    } else if (!selectedText) {
      return;
    }

    try {
      setAiLoading(true);
      setAiError(null);
      const queryText = selectedText || article?.summary || article?.title;
      const res = await apiService.analyzeReaderText(queryText, actionType);
      setAiResponse(res);
      setChatHistory([
        { role: 'assistant', content: res, timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }
      ]);
    } catch (err) {
      console.error('AI analysis error:', err);
      setAiError(err.message || 'AI request failed');
    } finally {
      setAiLoading(false);
    }
  };

  // Stepper navigation helpers
  const currentIndex = feed.findIndex(a => a.id === article?.id || a.slug === article?.slug);
  const prevArticle = currentIndex > 0 ? feed[currentIndex - 1] : null;
  const nextArticle = currentIndex >= 0 && currentIndex < feed.length - 1 ? feed[currentIndex + 1] : null;

  return (
    <div className="chapter-viewer-page">
      {/* Dynamic Head SEO & Meta Injection */}
      {article && (
        <SeoHead
          title={article.metaTitle || article.title}
          description={article.metaDescription || article.summary || `Read ${article.title} on Vedic Surgery.`}
          keywords={article.focusKeywords}
          canonicalUrl={article.canonicalUrl}
          ogImageUrl={article.ogImageUrl || article.coverImageUrl}
        />
      )}

      {/* Top Fixed Progress Bar */}
      <div className="reading-progress-track">
        <div className="reading-progress-fill" style={{ width: `${scrollProgress}%` }} />
      </div>

      {/* Control Toolbar */}
      <div className="cv-toolbar">
        <div className="cv-toolbar-inner">
          <nav className="cv-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/"><ArrowLeft size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Home</Link>
            <span className="separator">/</span>
            <Link to="/articles">Articles</Link>
            <span className="separator">/</span>
            <span className="current">{article?.category || 'Clinical'}</span>
          </nav>

          <div className="cv-controls">
            {/* Drawer Toggle */}
            <button
              type="button"
              className={`cv-control-btn ${tocOpen || mobileTocOpen ? 'active' : ''}`}
              onClick={() => {
                if (window.innerWidth <= 1024) {
                  setMobileTocOpen((v) => !v);
                  setMobileAiOpen(false);
                } else {
                  setTocOpen((v) => !v);
                }
              }}
              title="Toggle Articles Index"
            >
              <List size={16} />
              <span>Articles ({feed.length})</span>
            </button>

            {/* Font Size Adjusters */}
            <button
              type="button"
              className="cv-control-btn cv-font-btn"
              onClick={() => setFontSize((s) => Math.max(12, s - 1))}
              title="Decrease font size"
            >
              A-
            </button>
            <button
              type="button"
              className="cv-control-btn cv-font-btn"
              onClick={() => setFontSize((s) => Math.min(22, s + 1))}
              title="Increase font size"
            >
              A+
            </button>

            {/* Theme Selector */}
            <div className="cv-theme-selector">
              <button
                type="button"
                className={`cv-theme-btn ${theme === 'night' ? 'active' : ''}`}
                onClick={() => setTheme('night')}
                style={{ background: '#0b0f19' }}
                title="Night theme"
              >
                <Moon size={12} color="#ffffff" />
              </button>
              <button
                type="button"
                className={`cv-theme-btn ${theme === 'sepia' ? 'active' : ''}`}
                onClick={() => setTheme('sepia')}
                style={{ background: '#fbf0d9' }}
                title="Sepia theme"
              >
                <Sun size={12} color="#432818" />
              </button>
              <button
                type="button"
                className={`cv-theme-btn ${theme === 'light' ? 'active' : ''}`}
                onClick={() => setTheme('light')}
                style={{ background: '#ffffff' }}
                title="Light theme"
              >
                <Sun size={12} color="#000000" />
              </button>
              <button
                type="button"
                className={`cv-theme-btn ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => setTheme('dark')}
                style={{ background: '#0f172a' }}
                title="Dark theme"
              >
                <Moon size={12} color="#f8fafc" />
              </button>
            </div>

            {/* AI Assistant Drawer Trigger */}
            <button
              type="button"
              className={`cv-control-btn ${mobileAiOpen ? 'active' : ''}`}
              onClick={() => {
                if (window.innerWidth <= 1024) {
                  setMobileAiOpen((v) => !v);
                  setMobileTocOpen(false);
                } else if (!isAuthenticated()) {
                  setShowLoginPrompt(true);
                } else {
                  const aiWidget = document.querySelector('.cv-ai-widget');
                  if (aiWidget) {
                    aiWidget.scrollIntoView({ behavior: 'smooth' });
                  }
                }
              }}
            >
              <Sparkles size={16} color="var(--primary-color)" />
              <span>Vedic AI</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Backdrop Overlay for Drawers */}
      {(mobileTocOpen || mobileAiOpen) && (
        <div className="cv-mobile-backdrop" onClick={closeMobileDrawers} />
      )}

      {/* Main Reading Container Layout */}
      <div className={`cv-container ${!tocOpen ? 'toc-closed' : ''}`}>

        {/* Collapsible Left Articles TOC Sidebar / Mobile Drawer */}
        {(tocOpen || mobileTocOpen) && (
          <aside className={`cv-toc-sidebar ${mobileTocOpen ? 'mobile-open' : ''}`}>
            <div className="cv-toc-header">
              <h3>Articles Feed ({feed.length})</h3>
              <span className="cv-toc-badge">Published</span>
            </div>
            <div className="cv-toc-list">
              {feed.map((art, idx) => (
                <button
                  key={art.id}
                  type="button"
                  className={`cv-toc-item ${art.id === article?.id ? 'active' : ''}`}
                  onClick={() => {
                    closeMobileDrawers();
                    navigate(`/articles/${art.slug}`);
                  }}
                >
                  <span className="cv-toc-num">{idx + 1}.</span>
                  <span className="cv-toc-title">{art.title}</span>
                </button>
              ))}
            </div>
          </aside>
        )}

        {/* Center Primary Article Pane */}
        <main className="cv-main-pane">
          {loading ? (
            <div className="cv-article-card" style={{ textAlign: 'center', padding: '80px' }}>
              <div className="loader" style={{ margin: '0 auto 15px' }} />
              <p style={{ color: 'var(--text-secondary)' }}>Loading clinical article...</p>
            </div>
          ) : error ? (
            <div className="cv-article-card" style={{ textAlign: 'center', padding: '60px', color: '#ef4444' }}>
              <h3>⚠️ Article Not Found</h3>
              <p>{error}</p>
              <Link to="/articles" className="cv-control-btn active" style={{ display: 'inline-block', marginTop: '1rem' }}>
                Back to Articles Catalog
              </Link>
            </div>
          ) : article ? (
            <article
              ref={articleRef}
              className={`cv-article-card theme-${theme}`}
              onMouseUp={handleTextSelection}
              style={{
                ...(sidebarHeight ? { height: `${sidebarHeight}px`, maxHeight: `${sidebarHeight}px` } : {})
              }}
            >
              <header className="cv-article-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span className="cv-article-category">{article.category || 'Clinical'}</span>
                  <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>• {article.readingTimeMinutes || 8} min read</span>
                </div>

                <h1 className="cv-article-title">
                  {article.title}
                </h1>

                {article.subtitle && (
                  <h2 className="cv-article-subtitle">{article.subtitle}</h2>
                )}

                <div className="cv-article-meta">
                  <span>Author: {article.authorName || 'Vedic Surgery Specialist'}</span>
                  {article.authorRole && <span>({article.authorRole})</span>}
                  <span>•</span>
                  {article.publishedAt && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Calendar size={13} /> {new Date(article.publishedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                    </span>
                  )}
                  <span>•</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <Eye size={13} /> {article.viewCount || 0} views
                  </span>
                </div>
              </header>

              {/* Cover Image */}
              {article.coverImageUrl && (
                <div style={{ marginBottom: '2rem', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <img src={article.coverImageUrl} alt={article.title} style={{ width: '100%', maxHeight: '420px', objectFit: 'cover' }} />
                </div>
              )}

              {/* Main Indexable Article HTML Body */}
              <div
                className="cv-article-body"
                style={{ fontSize: `${fontSize}px` }}
                dangerouslySetInnerHTML={{ __html: article.contentHtml }}
              />

              {/* Guest AI Teaser Callout */}
              {!isAuthenticated() && (
                <div className="cv-guest-teaser">
                  <div className="cv-guest-teaser-content">
                    <h4>
                      <Sparkles size={16} style={{ marginRight: '6px' }} /> Unlock AI Article Analysis
                    </h4>
                    <p>Highlight any passage or medical term in this article to get instant AI translations and clinical commentary breakdowns.</p>
                  </div>
                  <Link to="/signup" className="cv-control-btn active" style={{ whiteSpace: 'nowrap' }}>
                    Sign Up Free <ArrowRight size={14} />
                  </Link>
                </div>
              )}

              {/* Responsive Bottom Ad Banner */}
              <div style={{ marginTop: '2.5rem' }}>
                <AdBanner type="leaderboard" />
              </div>

              {/* Article Stepper Footer */}
              <nav className="cv-stepper" aria-label="Article Navigation">
                {prevArticle ? (
                  <button
                    type="button"
                    className="cv-stepper-btn"
                    onClick={() => navigate(`/articles/${prevArticle.slug || prevArticle.id}`)}
                  >
                    <ChevronLeft size={20} />
                    <div className="meta">
                      <span className="label">Previous Article</span>
                      <span className="title">{prevArticle.title}</span>
                    </div>
                  </button>
                ) : (
                  <div />
                )}

                {nextArticle ? (
                  <button
                    type="button"
                    className="cv-stepper-btn"
                    onClick={() => navigate(`/articles/${nextArticle.slug || nextArticle.id}`)}
                    style={{ marginLeft: 'auto' }}
                  >
                    <div className="meta" style={{ textAlign: 'right' }}>
                      <span className="label">Next Article</span>
                      <span className="title">{nextArticle.title}</span>
                    </div>
                    <ChevronRight size={20} />
                  </button>
                ) : null}
              </nav>
            </article>
          ) : null}
        </main>

        {/* Right Sidebar Widget Column / Mobile Drawer */}
        <aside className={`cv-right-sidebar ${mobileAiOpen ? 'mobile-open' : ''}`} ref={rightSidebarRef}>
          {/* Dedicated Vedic AI Assistant Side Pane */}
          <div className="cv-sidebar-widget cv-ai-widget" style={{ border: '1px solid rgba(0, 212, 255, 0.25)', background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.85) 100%)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkles size={18} color="var(--primary-color)" />
                <h4 style={{ margin: 0, fontSize: '0.98rem', border: 'none', padding: 0 }}>Vedic AI Assistant</h4>
              </div>
              <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-color)', background: 'rgba(0, 212, 255, 0.1)', padding: '2px 6px', borderRadius: '4px' }}>
                Live
              </span>
            </div>

            {selectedText && (
              <div style={{ background: 'rgba(0,0,0,0.3)', padding: '8px 12px', borderRadius: '8px', marginBottom: '12px', borderLeft: '3px solid var(--primary-color)', fontSize: '0.84rem', lineHeight: 1.4 }}>
                <span style={{ fontSize: '0.7rem', opacity: 0.7, textTransform: 'uppercase', display: 'block', marginBottom: '2px' }}>Selected Passage</span>
                "{selectedText}"
              </div>
            )}

            <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="cv-control-btn"
                style={{ fontSize: '0.76rem', padding: '4px 8px' }}
                onClick={() => handleAiAction('translate')}
                disabled={aiLoading}
              >
                Translate Passage
              </button>
              <button
                type="button"
                className="cv-control-btn"
                style={{ fontSize: '0.76rem', padding: '4px 8px' }}
                onClick={() => handleAiAction('commentary')}
                disabled={aiLoading}
              >
                Commentary
              </button>
              <button
                type="button"
                className="cv-control-btn"
                style={{ fontSize: '0.76rem', padding: '4px 8px' }}
                onClick={() => handleAiAction('surgical')}
                disabled={aiLoading}
              >
                Surgical App
              </button>
            </div>

            {/* AI Response Output Area */}
            <div style={{ minHeight: '120px', maxHeight: '240px', overflowY: 'auto', background: 'rgba(0,0,0,0.25)', padding: '10px', borderRadius: '8px', fontSize: '0.86rem', lineHeight: 1.55, marginBottom: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
              {aiLoading ? (
                <div style={{ textAlign: 'center', padding: '15px 0' }}>
                  <p style={{ fontSize: '0.82rem', opacity: 0.8, margin: 0 }}>🧠 Analyzing article with Vedic AI...</p>
                </div>
              ) : aiError ? (
                <p style={{ color: '#ef4444', margin: 0, fontSize: '0.84rem' }}>⚠️ {aiError}</p>
              ) : aiResponse ? (
                <div>{aiResponse}</div>
              ) : chatHistory.length > 0 ? (
                <div>{chatHistory[0]?.content}</div>
              ) : (
                <p style={{ opacity: 0.7, margin: 0, fontSize: '0.82rem' }}>
                  Highlight any text in the article or click an action button above to get instant AI translations & surgical commentary.
                </p>
              )}
            </div>

            {/* Interactive Chat Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (chatMessage.trim()) {
                  handleAiAction(chatMessage);
                  setChatMessage('');
                }
              }}
              style={{ display: 'flex', gap: '6px' }}
            >
              <input
                type="text"
                placeholder="Ask follow-up question..."
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                style={{ flex: 1, background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', padding: '6px 10px', fontSize: '0.82rem', outline: 'none' }}
              />
              <button
                type="submit"
                className="cv-control-btn active"
                style={{ padding: '6px 10px', fontSize: '0.8rem' }}
                disabled={aiLoading || !chatMessage.trim()}
              >
                Ask AI
              </button>
            </form>
          </div>

          {/* Article Summary Card */}
          <div className="cv-sidebar-widget">
            <h4>Executive Summary</h4>
            <p style={{ fontSize: '0.86rem', opacity: 0.85, lineHeight: 1.6 }}>
              {article?.summary || 'Standardized clinical publication detailing surgical protocols, research findings, and procedural guidance.'}
            </p>
          </div>

          {/* Ad Banner Widget */}
          <div className="cv-sidebar-widget" style={{ padding: '0.5rem', textAlign: 'center' }}>
            <AdBanner type="square" />
          </div>
        </aside>

      </div>

      {/* Login Prompt Modal for Guests */}
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title="Sign in for AI Features"
        message="Create a free account to use Vedic AI Assistant, save reading bookmarks, and participate in surgical discussions."
      />
    </div>
  );
};

export default ArticleReader;
